import React, { useEffect, useMemo, useRef, useState } from 'react';
import classnames from 'classnames';
import { createSignal } from '@funtech-int/rival';

import { useKeyboard } from '@/shared/hooks/useKeyboard';
import { CarouselNav } from '@/shared/components/Carousel/CarouselNav';

import {
    AnimatedCarouselItemState,
    animateScrollLeft,
    applyAnimatedCarouselClick,
    createInitialItemStates,
    getModeFromItemState,
    getTargetScrollLeftPx,
} from './animatedCarouselMode';
import { AnimatedCarouselItem } from './_AnimatedCarouselItem';
import itemStyles from './_AnimatedCarouselItem.module.css';
import styles from './_AnimatedCarousel.module.css';

//  Константы эквиваленты css-переменным в файле стилей
const CAROUSEL_SLOT = 32.2;
const CAROUSEL_DURATION = 300;
const REM_PX = 10;

interface AnimatedCarouselProps<I> {
    items: I[];
    renderItem(
        item: I,
        index: number
    ): {
        key: string;
        active: React.ReactNode;
        default: React.ReactNode;
    };
    onActiveChange?: (activeIndex: number) => void;
}

const getInlinePadding = (el: HTMLElement) => {
    const cs = window.getComputedStyle(el);
    return {
        start: parseFloat(cs.paddingInlineStart || '0'),
        end: parseFloat(cs.paddingInlineEnd || '0'),
    };
};

const calculateNextScrollDistance = (
    scroller: HTMLElement,
    track: HTMLElement,
    itemSelector: string
): number => {
    const items = Array.from(track.querySelectorAll<HTMLElement>(itemSelector));
    if (items.length === 0) return 0;

    const { start: padStart, end: padEnd } = getInlinePadding(track);
    const { scrollLeft } = scroller;
    const viewport = scroller.clientWidth;
    const visibleRight = scrollLeft + viewport - padEnd;

    const target = items.find((item) => item.offsetLeft + item.offsetWidth > visibleRight + 1);
    if (!target) return 0;

    const targetScrollLeft = target.offsetLeft - padStart;
    const maxScrollLeft = scroller.scrollWidth - viewport;
    const clamped = Math.max(0, Math.min(targetScrollLeft, maxScrollLeft));
    return Math.max(0, clamped - scrollLeft);
};

const calculatePreviousScrollDistance = (
    scroller: HTMLElement,
    track: HTMLElement,
    itemSelector: string
): number => {
    const items = Array.from(track.querySelectorAll<HTMLElement>(itemSelector));
    if (items.length === 0) return 0;

    const { start: padStart, end: padEnd } = getInlinePadding(track);
    const { scrollLeft } = scroller;
    const viewport = scroller.clientWidth;
    const visibleLeft = scrollLeft + padStart;

    let target: HTMLElement | undefined;
    for (let i = items.length - 1; i >= 0; i--) {
        const item = items[i];
        if (item && item.offsetLeft < visibleLeft - 1) {
            target = item;
            break;
        }
    }
    if (!target) return 0;

    const targetScrollLeft = target.offsetLeft + target.offsetWidth - viewport + padEnd;
    const maxScrollLeft = scroller.scrollWidth - viewport;
    const clamped = Math.max(0, Math.min(targetScrollLeft, maxScrollLeft));
    return Math.max(0, scrollLeft - clamped);
};

export function AnimatedCarousel<I>(props: AnimatedCarouselProps<I>) {
    const { items, renderItem, onActiveChange } = props;

    const [itemStates, setItemStates] = useState<AnimatedCarouselItemState[]>(() => {
        const result = applyAnimatedCarouselClick({
            states: createInitialItemStates(items.length),
            activeIndex: null,
            clickedIndex: 0,
        });
        return result?.states ?? createInitialItemStates(items.length);
    });
    const [activeIndex, setActiveIndex] = useState<number>(0);

    const [scrollerElement, setScrollerElementState] = useState<HTMLDivElement | null>(null);
    const rootRef = useRef<HTMLDivElement>(null);
    const scrollerRef = useRef<HTMLDivElement | null>(null);
    const trackRef = useRef<HTMLUListElement>(null);

    const {
        scrollToPrevious,
        scrollToNext,
        startReachedSignal,
        endReachedSignal,
        handleMouseEnter,
        handleMouseLeave,
        setScrollerElement,
    } = useMemo(
        () => ({
            scrollToPrevious: () => {
                const scrollerEl = scrollerRef.current;
                const trackEl = trackRef.current;
                if (!scrollerEl || !trackEl) return;
                const distance = calculatePreviousScrollDistance(
                    scrollerEl,
                    trackEl,
                    `.${itemStyles.item}`
                );
                if (distance <= 0) return;
                scrollerEl.scrollBy({ left: -distance, behavior: 'smooth' });
            },
            scrollToNext: () => {
                const scrollerEl = scrollerRef.current;
                const trackEl = trackRef.current;
                if (!scrollerEl || !trackEl) return;
                const distance = calculateNextScrollDistance(
                    scrollerEl,
                    trackEl,
                    `.${itemStyles.item}`
                );
                if (distance <= 0) return;
                scrollerEl.scrollBy({ left: distance, behavior: 'smooth' });
            },
            handleMouseEnter: () => {
                rootRef.current?.classList.add(styles.carouselHovering);
            },
            handleMouseLeave: () => {
                rootRef.current?.classList.remove(styles.carouselHovering);
            },
            setScrollerElement: (element: HTMLDivElement | null) => {
                scrollerRef.current = element;
                setScrollerElementState(element);
            },
            startReachedSignal: createSignal(true),
            endReachedSignal: createSignal(true),
        }),
        []
    );

    const handleItemClick = (index: number) => {
        const result = applyAnimatedCarouselClick({
            states: itemStates,
            activeIndex,
            clickedIndex: index,
        });

        if (!result) {
            return;
        }

        setItemStates(result.states);
        setActiveIndex(result.activeIndex);
        onActiveChange?.(result.activeIndex);
    };

    useKeyboard({
        target: scrollerElement,
        onLeft: (event) => {
            event.preventDefault();
            if (activeIndex > 0) {
                handleItemClick(activeIndex - 1);
            }
        },
        onRight: (event) => {
            event.preventDefault();
            if (activeIndex < items.length - 1) {
                handleItemClick(activeIndex + 1);
            }
        },
    });

    useEffect(() => {
        if (!scrollerRef.current) {
            return undefined;
        }

        const slotPx = CAROUSEL_SLOT * REM_PX;
        const durationMs = CAROUSEL_DURATION;

        const targetLeft = getTargetScrollLeftPx(activeIndex, slotPx);

        return animateScrollLeft(scrollerRef.current, targetLeft, durationMs);
    }, [activeIndex]);

    useEffect(() => {
        if (!scrollerRef.current) return undefined;

        const element = scrollerRef.current;

        const update = () => {
            const maxScrollLeft = element.scrollWidth - element.clientWidth;
            startReachedSignal.set(element.scrollLeft <= 1);
            endReachedSignal.set(element.scrollLeft >= maxScrollLeft - 1);
        };

        update();
        element.addEventListener('scroll', update, { passive: true });
        const ro = new ResizeObserver(update);
        ro.observe(element);

        return () => {
            element.removeEventListener('scroll', update);
            ro.disconnect();
        };
    }, [startReachedSignal, endReachedSignal]);

    useEffect(() => {
        scrollerRef.current?.focus({ preventScroll: true });
    }, []);

    return (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div
            className={styles.root}
            ref={rootRef}
            //  Чтобы не пропадал фокус со скроллера при клике внутри карусели
            onMouseDown={(event) => {
                event.preventDefault();
            }}
        >
            <div className={styles.scrollerContainer}>
                <div
                    ref={setScrollerElement}
                    className={styles.scroller}
                    tabIndex={-1}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                >
                    <ul className={styles.track} ref={trackRef}>
                        {itemStates.map((state, index) => {
                            const item = items[index];

                            if (!item) {
                                return;
                            }

                            const renderedItem = renderItem(item, index);

                            return (
                                <AnimatedCarouselItem
                                    key={renderedItem.key}
                                    activeChild={renderedItem.active}
                                    defaultChild={renderedItem.default}
                                    mode={getModeFromItemState(state)}
                                    onClick={() => handleItemClick(index)}
                                />
                            );
                        })}
                    </ul>
                </div>
                <CarouselNav
                    className={classnames(styles.carouselNav, styles.carouselNavPrev)}
                    onClick={scrollToPrevious}
                    signal={startReachedSignal}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                />
                <CarouselNav
                    className={classnames(styles.carouselNav, styles.carouselNavNext)}
                    onClick={scrollToNext}
                    signal={endReachedSignal}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                />
            </div>
        </div>
    );
}
